import { useEffect, useState } from "react";
import { ModalContent, Card, Common, Icon } from "@/components";
import { useAppDispatch, useAppSelector } from "@/sharedStore/hooks";
import { manifest } from "../models";
import {
  routificSettingsLoadedSelector,
  routificSettingsLoadingSelector,
} from "../providers/deliveryManifest/selector";
import { getRoutificDefaultSettings } from "../providers/deliveryManifest/sliceServices";
import { ModalOptimizeConfigration } from "./ModalOptimizeConfigration";

interface Props {
  open: boolean;
  manifest?: manifest;
  manifests?: manifest[];
  isInFlight: boolean;
  onClose: () => void;
  onAdvanced: (manifest: manifest) => void;
  onAdvancedBulk?: (manifests: manifest[]) => void;
}

export const ModalReoptimiseRoute = ({
  open,
  manifest: manifestData,
  manifests,
  isInFlight,
  onClose,
  onAdvanced,
  onAdvancedBulk,
}: Props) => {
  const isBulkMode = Boolean(manifests?.length);
  const dispatch = useAppDispatch();
  const routificSettingsLoaded = useAppSelector(routificSettingsLoadedSelector);
  const routificSettingsLoading = useAppSelector(
    routificSettingsLoadingSelector
  );
  const [isOpenOptimizeConfigrationModal, setIsOpenOptimizeConfigrationModal] =
    useState(false);

  useEffect(() => {
    if (open && !routificSettingsLoaded && !routificSettingsLoading) {
      dispatch(getRoutificDefaultSettings());
    }
  }, [open, routificSettingsLoaded, routificSettingsLoading, dispatch]);

  const buttonsDisabled = isInFlight || !routificSettingsLoaded;

  const handleAdvanced = () => {
    if (buttonsDisabled) return;

    if (isBulkMode) {
      if (!manifests?.length || !onAdvancedBulk) return;
      onAdvancedBulk(manifests);
      onClose();
      return;
    }

    if (!manifestData) return;
    onAdvanced(manifestData);
    onClose();
  };

  return (
    <>
      <ModalContent
        open={open}
        onClose={onClose}
        panelClassWidth="max-w-[43.75rem] w-full"
      >
        <Card
          title={
            isBulkMode
              ? "Advanced reoptimise"
              : `Advanced reoptimise - ${manifestData?.route?.name ?? ""}`
          }
        >
          <div className="p-4">
            <div className="text-sm text-neutral-60">
              Runs a full optimisation honouring time windows, capacity, tags,
              and balance settings. Some stops may be moved to{" "}
              <b>Unallocated</b> if they can&apos;t fit.
            </div>
            {isInFlight && (
              <p className="text-sm text-warning-60 mt-3">
                {isBulkMode
                  ? "One or more selected routes are already being reoptimised — wait for them to finish."
                  : "This route is already being reoptimised — open another route or wait for it to finish."}
              </p>
            )}
            <p className="text-sm text-neutral-60 mt-3">
              Use <b>Set up configuration</b> to edit the optimisation settings
              used by Advanced.
            </p>
          </div>
          <div className="shadow-overline px-4 py-3 flex space-x-2 justify-end items-center">
            <div className="flex-1 flex items-center">
              <div className="border-r border-neutral-20 pr-2 mr-2">
                <Common.Button
                  color="transparent"
                  onClick={() => setIsOpenOptimizeConfigrationModal(true)}
                  disabled={!routificSettingsLoaded}
                >
                  <div className="text-blue font-semibold flex items-center">
                    {routificSettingsLoading && !routificSettingsLoaded ? (
                      <Icon.Loading className="mr-2" />
                    ) : (
                      <Icon.Settings className="fill-blue mr-2" />
                    )}
                    Set up configuration
                  </div>
                </Common.Button>
              </div>
            </div>
            <Common.Button onClick={handleAdvanced} disabled={buttonsDisabled}>
              Advanced Optimize
            </Common.Button>
          </div>
        </Card>
      </ModalContent>
      <ModalOptimizeConfigration
        open={isOpenOptimizeConfigrationModal}
        onClose={() => setIsOpenOptimizeConfigrationModal(false)}
      />
    </>
  );
};
